/**
 * Site Settings — the site-wide singleton configuration contract.
 *
 * One record governs the public site's identity (name),
 * postal address, social presence and the footer's authored copy. There is no
 * create / list / delete — only read
 * and partial update. This module is the single source of truth for the shape,
 * the DEFAULTS and the update rules, consumed by:
 *  - the API settings Lambda (server-side validation on PATCH),
 *  - the CMS settings stub + SettingsPage (client-side mirror validation), and
 *  - `@diality/ui`'s marketing chrome, which derives its built-in copy from
 *    DEFAULT_SITE_SETTINGS so "a footer with no props" and "a fresh install"
 *    show the same words.
 *
 * Validation is atomic: `validateSiteSettingsUpdate` merges the partial change
 * onto the current settings and returns either the fully-merged value or the
 * list of field errors — never a partial write. Callers persist `value` only
 * when `ok` is true, so a rejected save writes nothing.
 */
import { DEFAULT_LOCALE } from "./page";
/** The complete R1 catalog, in picker order. */
export const SOCIAL_PLATFORM_IDS = [
    "facebook",
    "instagram",
    "linkedin",
    "x",
    "youtube",
    "tiktok",
    "reddit",
    "pinterest",
    "snapchat",
    "whatsapp",
    "telegram",
    "twitch",
];
export const SOCIAL_PLATFORM_LABELS = {
    facebook: "Facebook",
    instagram: "Instagram",
    linkedin: "LinkedIn",
    x: "X",
    youtube: "YouTube",
    tiktok: "TikTok",
    reddit: "Reddit",
    pinterest: "Pinterest",
    snapchat: "Snapchat",
    whatsapp: "WhatsApp",
    telegram: "Telegram",
    twitch: "Twitch",
};
/** A public tag identifier, never a URL or executable snippet. Blank disables. */
export function isGa4MeasurementId(value) {
    return typeof value === "string" && (value === "" || /^G-[A-Z0-9]+$/.test(value));
}
/** The R1 default singleton, returned when nothing has been stored yet. */
export const DEFAULT_SITE_SETTINGS = {
    faviconAssetId: "",
    siteName: "Diality",
    address: "181 Technology Dr #150, Irvine, CA 92618",
    socialLinks: [
        { platform: "facebook", url: "https://facebook.com/diality" },
        { platform: "instagram", url: "https://instagram.com/diality" },
        { platform: "linkedin", url: "https://linkedin.com/company/diality" },
    ],
    footer: {
        newsletterHeading: "Subscribe to learn about our latest news",
        newsletterPlaceholder: "Enter your email",
        newsletterSubmitLabel: "Submit",
        copyrightNotice: "Diality, the Diality logo, the Moda-flx Hemodialysis System, and the Moda-flx Hemodialysis System logo are trademarks of Diality, Inc. The Moda-flx Hemodialysis System is indicated for use in patients with acute and/or chronic renal failure, with or without ultrafiltration, in an acute, post-acute, or chronic care facility. Treatments must be administered under a physician's prescription, by a trained person who is considered competent in the use of the device.",
    },
    productDisclaimers: [{
            body: "This product is not approved for sale in any region. Performance characteristics of this product have not been established and all performance claims are project goals only.",
            pageId: "product",
        }],
    supportedLocales: [DEFAULT_LOCALE],
    localizations: {},
};
/** Upgrade older API responses and cached settings without resurrecting cleared lists. */
export function normalizeProductDisclaimers(settings) {
    if (settings.productDisclaimers !== undefined) {
        return settings.productDisclaimers.map((entry) => ({ ...entry }));
    }
    return [{ ...DEFAULT_SITE_SETTINGS.productDisclaimers[0], ...settings.productDisclaimer }];
}
/**
 * A usable web URL — an http(s) scheme followed by a host. Rejects
 * scheme-less values (`facebook.com`, `linkedin`) and non-web schemes (`ftp://x`).
 */
function isValidUrl(value) {
    return /^https?:\/\/(?:[^\s:@/]+(?::[^\s@/]*)?@)?(?:\[[0-9a-f:.]+\]|[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?)(?::\d+)?(?:[/?#][^\s]*)?$/i.test(value);
}
/**
 * Merge a partial string-group change onto the current group, trimming provided
 * values.
 *
 * The base is `fallback` FIRST, so a record written before the group existed
 * still merges to a COMPLETE group rather than an empty one. Two real sources
 * produce such a record: an item stored by an earlier deploy and a stale
 * `sessionStorage` singleton in an open CMS tab. Without
 * this base every value would merge to "" and every save would be rejected.
 *
 * A deliberately-cleared "" in `current` is an own property, so it survives.
 */
function mergeStringGroup(current, fallback, changes) {
    const merged = { ...fallback, ...(current ?? {}) };
    for (const key of Object.keys(fallback)) {
        const next = changes?.[key];
        if (next !== undefined)
            merged[key] = next.trim();
    }
    return merged;
}
/**
 * One scalar string, merged on the same terms as `mergeStringGroup`: a provided
 * change wins (trimmed), an absent one keeps what is stored — and a record
 * written before the field existed falls back to the SHIPPED DEFAULT rather
 * than to `undefined`. Without that last step the required check would fire on
 * a value the author never had a chance to set, and no save against a legacy
 * record could ever succeed.
 */
function mergeString(current, fallback, change) {
    if (change !== undefined)
        return change.trim();
    return current ?? fallback;
}
/** Normalize a locale list: trim, drop blanks, dedupe. */
function normalizeLocales(locales) {
    const seen = new Set();
    for (const locale of locales) {
        const code = locale.trim();
        if (code)
            seen.add(code);
    }
    return [...seen];
}
function cloneLocalizations(localizations) {
    return Object.fromEntries(Object.entries(localizations).map(([locale, value]) => [
        locale,
        {
            ...(value.siteName !== undefined ? { siteName: value.siteName } : {}),
            ...(value.footer ? { footer: { ...value.footer } } : {}),
        },
    ]));
}
/**
 * The free-text values that must not be blank, checked in this order.
 *
 * Appended AFTER the site-name / social-profile / locale checks on
 * purpose: the API surfaces `errors[0].field` as the response's `field`, so
 * adding these must not change which field a pre-existing failure reports.
 */
const REQUIRED_TEXT_FIELDS = [
    {
        field: "address",
        read: (s) => s.address,
        message: "The contact address is required",
    },
    {
        field: "footer.newsletterHeading",
        read: (s) => s.footer.newsletterHeading,
        message: "The newsletter invitation is required",
    },
    {
        field: "footer.newsletterPlaceholder",
        read: (s) => s.footer.newsletterPlaceholder,
        message: "The newsletter email placeholder is required",
    },
    {
        field: "footer.newsletterSubmitLabel",
        read: (s) => s.footer.newsletterSubmitLabel,
        message: "The newsletter button label is required",
    },
    {
        field: "footer.copyrightNotice",
        read: (s) => s.footer.copyrightNotice,
        message: "The copyright and trademark notice is required",
    },
];
/**
 * Merge `changes` onto `current` and validate the result.
 *
 * Rules (all applied to the TRIMMED value):
 *  - `siteName` is required (cannot be blank).
 *  - social profiles are unique supported platforms with non-empty http(s) URLs.
 *  - the contact address and every footer copy value are required — the site's
 *    chrome and its legal copy always exist.
 *  - `supportedLocales` is normalized (trimmed, deduped) and must always
 *    contain the default locale — English is the visitor's fallback, so it
 *    can never be removed.
 *
 * Returns the fully-merged settings when valid, or every field error when not —
 * so the caller writes all-or-nothing.
 */
export function validateSiteSettingsUpdate(current, changes) {
    const errors = [];
    // Validate the wire value before calling trim: PATCH bodies are untrusted.
    if (changes.ga4MeasurementId !== undefined &&
        (typeof changes.ga4MeasurementId !== "string" ||
            !isGa4MeasurementId(changes.ga4MeasurementId.trim()))) {
        errors.push({
            field: "ga4MeasurementId", code: "invalidMeasurementId",
            message: "Enter a GA4 measurement ID starting with G- followed by uppercase letters and numbers, or leave blank to disable analytics",
        });
    }
    const measurementId = typeof changes.ga4MeasurementId === "string"
        ? changes.ga4MeasurementId.trim()
        : current.ga4MeasurementId;
    const merged = {
        ...(measurementId !== undefined ? { ga4MeasurementId: measurementId } : {}),
        faviconAssetId: mergeString(current.faviconAssetId, "", changes.faviconAssetId),
        siteName: changes.siteName !== undefined
            ? changes.siteName.trim()
            : current.siteName,
        address: mergeString(current.address, DEFAULT_SITE_SETTINGS.address, changes.address),
        socialLinks: (changes.socialLinks ?? current.socialLinks ?? DEFAULT_SITE_SETTINGS.socialLinks).map((link) => ({ ...link, url: typeof link.url === "string" ? link.url.trim() : link.url })),
        footer: mergeStringGroup(current.footer, DEFAULT_SITE_SETTINGS.footer, changes.footer),
        productDisclaimers: Array.isArray(changes.productDisclaimers)
            ? changes.productDisclaimers.map((entry) => ({
                body: typeof entry?.body === "string" ? entry.body.trim() : "",
                pageId: typeof entry?.pageId === "string" ? entry.pageId.trim() : "",
            }))
            : normalizeProductDisclaimers(current),
        supportedLocales: normalizeLocales(changes.supportedLocales ?? current.supportedLocales),
        localizations: cloneLocalizations(changes.localizations ?? current.localizations ?? {}),
    };
    if (merged.faviconAssetId && !/^[a-zA-Z0-9_-]+$/.test(merged.faviconAssetId)) {
        errors.push({ field: "faviconAssetId", code: "invalidUrl", message: "Choose a valid favicon upload" });
    }
    if (!merged.siteName) {
        errors.push({
            field: "siteName",
            code: "required",
            message: "Site name is required",
        });
    }
    if (merged.socialLinks.length > SOCIAL_PLATFORM_IDS.length) {
        errors.push({
            field: "socialLinks",
            code: "tooMany",
            message: `No more than ${SOCIAL_PLATFORM_IDS.length} social profiles may be added`,
        });
    }
    const selectedPlatforms = new Set();
    merged.socialLinks.forEach((link, index) => {
        const platform = link.platform;
        if (!SOCIAL_PLATFORM_IDS.includes(platform)) {
            errors.push({
                field: `socialLinks.${index}.platform`,
                code: "unsupported",
                message: "Choose a supported social platform",
            });
        }
        else if (selectedPlatforms.has(platform)) {
            errors.push({
                field: `socialLinks.${index}.platform`,
                code: "duplicate",
                message: `${SOCIAL_PLATFORM_LABELS[platform]} can only be added once`,
            });
        }
        selectedPlatforms.add(platform);
        if (!link.url) {
            errors.push({
                field: `socialLinks.${index}.url`,
                code: "required",
                message: "A profile URL is required",
            });
        }
        else if (!isValidUrl(link.url)) {
            errors.push({
                field: `socialLinks.${index}.url`,
                code: "invalidUrl",
                message: `${SOCIAL_PLATFORM_LABELS[link.platform] ?? "Social profile"} must use a valid HTTP(S) URL`,
            });
        }
    });
    if (!merged.supportedLocales.includes(DEFAULT_LOCALE)) {
        errors.push({
            field: "supportedLocales",
            code: "required",
            message: "The default locale (English) cannot be removed",
        });
    }
    for (const { field, read, message } of REQUIRED_TEXT_FIELDS) {
        if (!read(merged)) {
            errors.push({ field, code: "required", message });
        }
    }
    if (changes.productDisclaimers !== undefined && !Array.isArray(changes.productDisclaimers)) {
        errors.push({ field: "productDisclaimers", code: "invalidType", message: "Disclaimers must be a list" });
    }
    const assignedPages = new Set();
    merged.productDisclaimers.forEach((entry, index) => {
        for (const key of ["body", "pageId"]) {
            if (!entry[key]) {
                errors.push({
                    field: `productDisclaimers.${index}.${key}`,
                    code: "required",
                    message: key === "body" ? "Disclaimer copy is required" : "Choose a page for the disclaimer",
                });
            }
        }
        if (entry.pageId && assignedPages.has(entry.pageId)) {
            errors.push({ field: `productDisclaimers.${index}.pageId`, code: "duplicate", message: "This page already has a disclaimer" });
        }
        assignedPages.add(entry.pageId);
    });
    if (errors.length > 0)
        return { ok: false, errors };
    return { ok: true, value: merged };
}
/**
 * The footer's copyright paragraph: "© {year} {siteName}. {copyrightNotice}".
 *
 * The prefix is template-owned so an author never types a year that will go
 * stale, and the authored notice supplies only the trademark/indications body.
 *
 * Returned as ONE string rather than composed in JSX: React separates adjacent
 * JSX text expressions with comment markers in SSR output, and building the
 * sentence here keeps it a single text node — for screen readers, for
 * copy-paste, and for any matcher reading it as one contiguous run.
 */
export function formatCopyrightNotice(settings, year) {
    const name = settings.siteName.trim();
    // "Diality, Inc." already ends the sentence — the story's own third example
    // renames the site to exactly that — so adding the template's period would
    // read "© 2026 Diality, Inc.. ".
    const stop = /[.!?]$/.test(name) ? "" : ".";
    return `© ${year} ${name}${stop} ${settings.footer.copyrightNotice}`;
}
//# sourceMappingURL=site-settings.js.map